import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import PageHeader from "../components/layout/PageHeader";
import { toast } from "sonner";
import { Bookmark, BookmarkPlus, Trash2, Edit2, Search, Filter, FileText, Loader2 } from "lucide-react";

const Bookmarks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isEnglish = language === "en";

  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [colorFilter, setColorFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedBookmark, setSelectedBookmark] = useState(null);
  const [editData, setEditData] = useState({ color: "", note: "" });
  const [saving, setSaving] = useState(false);

  const highlightColors = [
    { value: "yellow", label: isEnglish ? "Yellow" : "Kuning", class: "bg-yellow-200 border-yellow-500 dark:bg-yellow-900/25 dark:border-yellow-400" },
    { value: "green", label: isEnglish ? "Green" : "Hijau", class: "bg-emerald-200 border-emerald-500 dark:bg-emerald-900/25 dark:border-emerald-400" },
    { value: "blue", label: isEnglish ? "Blue" : "Biru", class: "bg-blue-200 border-blue-500 dark:bg-blue-900/25 dark:border-blue-400" },
    { value: "red", label: isEnglish ? "Red" : "Merah", class: "bg-rose-200 border-rose-500 dark:bg-rose-900/25 dark:border-rose-400" },
    { value: "purple", label: isEnglish ? "Purple" : "Ungu", class: "bg-purple-200 border-purple-500 dark:bg-purple-900/25 dark:border-purple-400" },
  ];

  useEffect(() => {
    loadBookmarks();
  }, [colorFilter, sortBy, sortOrder]);

  const loadBookmarks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (colorFilter !== "all") params.color = colorFilter;
      params.sort_by = sortBy;
      params.sort_order = sortOrder;

      const res = await apiClient.get("/bookmarks", { params });
      setBookmarks(res.data);
    } catch (error) {
      console.error("Error loading bookmarks:", error);
      toast.error(isEnglish ? "Failed to load bookmarks" : "Gagal memuat bookmark");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (bookmark) => {
    setSelectedBookmark(bookmark);
    setEditData({
      color: bookmark.color || "",
      note: bookmark.note || ""
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const updateData = {};
      if (editData.color) updateData.color = editData.color;
      if (editData.note) updateData.note = editData.note;

      const res = await apiClient.patch(`/bookmarks/${selectedBookmark.bookmark_id}`, updateData);
      setBookmarks(bookmarks.map(b => b.bookmark_id === selectedBookmark.bookmark_id ? res.data : b));
      toast.success(isEnglish ? "Bookmark updated" : "Bookmark diperbarui");
      setShowEditDialog(false);
      setSelectedBookmark(null);
      setEditData({ color: "", note: "" });
    } catch (error) {
      console.error("Error updating bookmark:", error);
      toast.error(isEnglish ? "Failed to update bookmark" : "Gagal memperbarui bookmark");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bookmarkId) => {
    if (!confirm(isEnglish ? "Delete this bookmark?" : "Hapus bookmark ini?")) return;

    try {
      await apiClient.delete(`/bookmarks/${bookmarkId}`);
      setBookmarks(bookmarks.filter(b => b.bookmark_id !== bookmarkId));
      toast.success(isEnglish ? "Bookmark deleted" : "Bookmark dihapus");
    } catch (error) {
      console.error("Error deleting bookmark:", error);
      toast.error(isEnglish ? "Failed to delete bookmark" : "Gagal menghapus bookmark");
    }
  };

  const handleNavigateToAyah = (surahNumber, ayahNumber) => {
    navigate(`/quran/${surahNumber}?ayah=${ayahNumber}`);
  };

  const filteredBookmarks = bookmarks.filter(b => {
    const searchLower = searchQuery.toLowerCase();
    return (
      b.surah_name?.toLowerCase().includes(searchLower) ||
      b.note?.toLowerCase().includes(searchLower) ||
      b.ayah_text?.toLowerCase().includes(searchLower)
    );
  });

  const exportBookmarks = () => {
    const exportData = filteredBookmarks.map(b => ({
      surah: b.surah_name,
      ayah: b.ayah_number,
      text: b.ayah_text,
      color: b.color || "-",
      note: b.note || "-",
      created: new Date(b.created_at).toLocaleDateString(isEnglish ? "en-US" : "id-ID")
    }));

    const text = exportData.map((b, i) =>
      `${i + 1}. ${b.surah} - Ayat ${b.ayah}\n` +
      `   ${b.text?.substring(0, 100)}...\n` +
      `   Color: ${b.color} | Note: ${b.note}\n` +
      `   Created: ${b.created}\n`
    ).join("\n");

    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quran-bookmarks-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success(isEnglish ? "Bookmarks exported" : "Bookmark diekspor");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      <PageHeader title={isEnglish ? "My Bookmarks" : "Bookmark Saya"} />

      <div className="page-container space-y-4">
        {/* Stats */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Bookmark className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {isEnglish ? "Total Bookmarks" : "Total Bookmark"}
                  </p>
                  <p className="text-2xl font-bold">{filteredBookmarks.length}</p>
                </div>
              </div>
              <Button onClick={exportBookmarks} variant="outline" size="sm">
                <FileText className="w-4 h-4 mr-2" />
                {isEnglish ? "Export" : "Ekspor"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isEnglish ? "Search bookmarks..." : "Cari bookmark..."}
                className="flex-1"
              />
            </div>

            <div className="flex gap-2">
              <div className="flex items-center gap-2 flex-1">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <Select value={colorFilter} onValueChange={setColorFilter}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{isEnglish ? "All Colors" : "Semua Warna"}</SelectItem>
                    {highlightColors.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Select value={`${sortBy}-${sortOrder}`} onValueChange={(v) => {
                const [s, o] = v.split("-");
                setSortBy(s);
                setSortOrder(o);
              }}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at-desc">{isEnglish ? "Newest" : "Terbaru"}</SelectItem>
                  <SelectItem value="created_at-asc">{isEnglish ? "Oldest" : "Terlama"}</SelectItem>
                  <SelectItem value="surah_number-asc">{isEnglish ? "Surah Order" : "Urut Surah"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Bookmarks List */}
        {filteredBookmarks.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <BookmarkPlus className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {isEnglish ? "No bookmarks yet" : "Belum ada bookmark"}
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate("/quran")}
              >
                {isEnglish ? "Go to Quran" : "Ke Quran"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredBookmarks.map((bookmark) => {
              const colorClass = bookmark.color ? highlightColors.find(c => c.value === bookmark.color)?.class : "";

              return (
                <Card
                  key={bookmark.bookmark_id}
                  className={`${colorClass} cursor-pointer hover:shadow-md transition-shadow`}
                  onClick={() => handleNavigateToAyah(bookmark.surah_number, bookmark.ayah_number)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="shrink-0">
                            {bookmark.surah_name}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {isEnglish ? "Ayat" : "Ayat"} {bookmark.ayah_number}
                          </span>
                          {bookmark.color && (
                            <div className={`w-4 h-4 rounded ${highlightColors.find(c => c.value === bookmark.color)?.class}`} />
                          )}
                        </div>

                        {bookmark.ayah_text && (
                          <p className="text-sm text-right mb-2 text-foreground" dir="rtl" style={{ fontFamily: "Amiri, serif" }}>
                            {bookmark.ayah_text.substring(0, 150)}
                            {bookmark.ayah_text.length > 150 && "..."}
                          </p>
                        )}

                        {bookmark.note && (
                          <div className="bg-background/60 dark:bg-background/80 p-2 rounded text-sm text-muted-foreground italic">
                            "{bookmark.note}"
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(bookmark.created_at).toLocaleDateString(isEnglish ? "en-US" : "id-ID", {
                            year: "numeric",
                            month: "long",
                            day: "numeric"
                          })}
                        </p>
                      </div>

                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(bookmark)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(bookmark.bookmark_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isEnglish ? "Edit Bookmark" : "Ubah Bookmark"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {isEnglish ? "Highlight Color" : "Warna Highlight"}
              </label>
              <div className="flex gap-2">
                {highlightColors.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setEditData({ ...editData, color: editData.color === color.value ? "" : color.value })}
                    className={`w-10 h-10 rounded-lg border-2 ${color.class} ${
                      editData.color === color.value ? "ring-2 ring-offset-2 ring-primary" : ""
                    } transition-all`}
                    title={color.label}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {isEnglish ? "Notes" : "Catatan"}
              </label>
              <Textarea
                value={editData.note}
                onChange={(e) => setEditData({ ...editData, note: e.target.value })}
                placeholder={isEnglish ? "Add your notes..." : "Tambahkan catatan..."}
                className="min-h-[100px] resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={saving}>
              {isEnglish ? "Cancel" : "Batal"}
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEnglish ? "Saving..." : "Menyimpan..."}
                </>
              ) : (
                isEnglish ? "Save" : "Simpan"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bookmarks;
